import React, { type SyntheticEvent } from 'react';
import {
	VIEW_KIND_BOARD,
	VIEW_KIND_TABLE,
	VIEW_KIND_MATRIX,
	VIEW_KIND_TIMELINE,
	VIEW_KIND_TREE,
} from '@jira/polaris__domain-view/src/viewKindTimeline.constants.tsx';
import ButtonItem from '@atlaskit/menu/button-item';
import Section from '@atlaskit/menu/section';
import { token } from '@atlaskit/tokens';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { ViewItemIcon } from '../../view-item-icon/index.tsx';
import { useIsViewKindLocked } from '../../../common/utils/useIsViewKindLocked.tsx';
import { ViewItemTooltip } from './ViewItemTooltip.tsx';
import { ViewItemIconAfter } from './ViewItemIconAfter.tsx';

const createNewList = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.create-view-menu.menu.create-new-list',
	defaultMessage: 'List view',
	description: 'Dropdown option to create a new JPD list view',
});

const createNewBoard = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.create-view-menu.menu.create-new-board',
	defaultMessage: 'Board view',
	description: 'Dropdown option to create a new JPD board view',
});

const createNewMatrix = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.create-view-menu.menu.create-new-matrix',
	defaultMessage: 'Matrix view',
	description: 'Dropdown option to create a new JPD matrix view',
});

const createNewTimeline = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.create-view-menu.menu.create-new-timeline',
	defaultMessage: 'Timeline view',
	description: 'Dropdown option to create a new JPD timeline view',
});

const createNewTree = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.create-view-menu.menu.create-new-tree',
	defaultMessage: 'Tree view',
	description: 'Dropdown option to create a new JPD tree view',
});

type BasicViewsProps = {
	createViewHandlers: {
		readonly TABLE: (event: SyntheticEvent) => void;
		readonly BOARD: (event: SyntheticEvent) => void;
		readonly TIMELINE: (event: SyntheticEvent) => void;
		readonly MATRIX: (event: SyntheticEvent) => void;
		readonly TREE: (event: SyntheticEvent) => void;
	};
	hasReachedViewsLimit: number | null;
};

const itemsConfigOld = [
	{
		viewKind: VIEW_KIND_TABLE,
		message: createNewList,
		testId:
			'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.create-view-menu.menu.table',
	},
	{
		viewKind: VIEW_KIND_BOARD,
		message: createNewBoard,
		testId:
			'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.create-view-menu.menu.board',
	},
	{
		viewKind: VIEW_KIND_TIMELINE,
		message: createNewTimeline,
		testId:
			'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.create-view-menu.menu.timeline',
	},
	{
		viewKind: VIEW_KIND_MATRIX,
		message: createNewMatrix,
		testId:
			'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.create-view-menu.menu.matrix',
	},
] as const;

const itemsConfig = [
	{
		viewKind: VIEW_KIND_TABLE,
		message: createNewList,
		testId:
			'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.create-view-menu.menu.table',
	},
	{
		viewKind: VIEW_KIND_BOARD,
		message: createNewBoard,
		testId:
			'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.create-view-menu.menu.board',
	},
	{
		viewKind: VIEW_KIND_TIMELINE,
		message: createNewTimeline,
		testId:
			'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.create-view-menu.menu.timeline',
	},
	{
		viewKind: VIEW_KIND_MATRIX,
		message: createNewMatrix,
		testId:
			'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.create-view-menu.menu.matrix',
	},
	{
		viewKind: VIEW_KIND_TREE,
		message: createNewTree,
		testId:
			'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.create-view-menu.menu.tree',
	},
] as const;

export const BasicViewsList: React.FC<BasicViewsProps> = ({
	createViewHandlers,
	hasReachedViewsLimit,
}: BasicViewsProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const isViewKindLocked = useIsViewKindLocked();

	const items = fg('jpd_tree-view')
		? itemsConfig.filter(({ viewKind }) => !isViewKindLocked(viewKind))
		: itemsConfigOld;

	return (
		<Section isList>
			{/* switching to default itemsConfig because tree view option is always visible, disabled for non-premium users */}
			{(fg('jpd_tree-view-standard-upgrade') ? itemsConfig : items).map(
				({ viewKind, message, testId }) => (
					<ViewItemTooltip key={viewKind} viewKind={viewKind}>
						<ButtonItem
							testId={testId}
							onClick={createViewHandlers[viewKind]}
							isDisabled={!!hasReachedViewsLimit || isViewKindLocked(viewKind)}
							iconBefore={
								<ViewItemIcon
									viewKind={viewKind}
									color={hasReachedViewsLimit ? token('color.icon.disabled') : undefined}
									label=""
								/>
							}
							iconAfter={<ViewItemIconAfter viewKind={viewKind} />}
						>
							{formatMessage(message)}
						</ButtonItem>
					</ViewItemTooltip>
				),
			)}
		</Section>
	);
};
